/**
 * "Ajustar recorte" (E141.F2, GUS-147, ADR-0079 E.26): o perito corrige o recorte e a orientação da
 * fotografia escolhida, sobre o ORIGINAL, e a conferência facial é refeita ao salvar.
 *
 * Reusa o editor da galeria (`RecorteDaFoto`, E80.F1): a caixa e o giro viajam como geometria, e o
 * arquivo original continua intacto no servidor (§2.2). O que esta peça acrescenta é o lugar na
 * pré-análise, o salvamento pela rota do recorte e o retorno: salvo (a seção relê e acompanha a
 * reconferência), recusado porque o exame foi concluído, ou falha.
 */
import { useState } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { ExameEletronicoError } from '@/lib/exame_eletronico_erros';
import {
  recortarFotoDoSignatario,
  type CaixaDoRecorte,
  type FotoDoSignatarioData,
  type RotacaoDaFoto,
} from '@/lib/fotos_do_signatario';

import { RecorteDaFoto } from './RecorteDaFoto';

const t = ptBR.exameEletronico.fotografias;

function mensagemDoErro(err: unknown): string {
  if (err instanceof ExameEletronicoError && err.reason === 'concluido') return t.ajusteBloqueado;
  return t.erroAjuste;
}

export interface AjustarRecorteProps {
  caseId: string;
  foto: FotoDoSignatarioData;
  /** O recorte foi salvo: a seção relê e acompanha a conferência refeita. */
  onAjustada: () => void;
  onFechar: () => void;
}

export function AjustarRecorte({
  caseId,
  foto,
  onAjustada,
  onFechar,
}: AjustarRecorteProps): JSX.Element {
  const client = useApiClient();
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const aplicar = (caixa: CaixaDoRecorte | null, rotacao: RotacaoDaFoto): void => {
    void (async () => {
      setSalvando(true);
      setErro(null);
      try {
        await recortarFotoDoSignatario(client, caseId, foto.id, {
          bounding_box: caixa,
          rotacao_deg: rotacao,
        });
        onAjustada();
      } catch (err) {
        setErro(mensagemDoErro(err));
      } finally {
        setSalvando(false);
      }
    })();
  };

  return (
    <div
      className="space-y-sc-3 border-t border-border pt-sc-4"
      data-testid="ajustar-recorte"
      data-grupo={foto.grupo}
      aria-labelledby="ajustar-recorte-titulo"
    >
      <div className="flex flex-wrap items-center justify-between gap-sc-2">
        <h4 id="ajustar-recorte-titulo" className="text-body text-fg">
          {t.ajusteTitulo(t.grupos[foto.grupo])}
        </h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onFechar}
          data-testid="ajustar-recorte-fechar"
        >
          {t.fecharAjuste}
        </Button>
      </div>
      <p className="max-w-prose text-body-sm text-fg-muted">{t.ajusteIntro}</p>
      {erro !== null ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid="ajustar-recorte-erro">
          {erro}
        </p>
      ) : null}
      <RecorteDaFoto
        caseId={caseId}
        foto={foto}
        salvando={salvando}
        onAplicar={aplicar}
        onCancelar={onFechar}
      />
    </div>
  );
}
